import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { ITINERARY_FOOD_INVOICE_PATH } from '../../../../config/paths';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import { tzNormalizeDate } from '../../../../utils/date';
import { INT_DATE_FORMAT } from '../../../../config/locale';
import {
  clearItineraryFoodInvoice,
  getItineraryFoodInvoice,
  putItineraryFoodInvoice,
} from '../../../../actions/food/ItineraryFoodInvoice';
import ItineraryFoodInvoiceForm from './ItineraryFoodInvoiceForm';

const EditItineraryFoodInvoice = ({
  breadcrumbs,
  dispatchGetItineraryFoodInvoice,
  dispatchClearItineraryFoodInvoice,
  dispatchPutItineraryFoodInvoice,
  match: {
    params: { id: itineraryFoodInvoiceId },
  },
  loading,
  itineraryFoodInvoice,
}) => {
  useLayoutEffect(() => {
    dispatchGetItineraryFoodInvoice({ itineraryFoodInvoiceId });

    return () => dispatchClearItineraryFoodInvoice();
  }, []);

  const generateInitialValues = (invoiceJson) => ({
    accountingDeliveredDate: invoiceJson.accountingDeliveredDate
      ? tzNormalizeDate({
          date: invoiceJson.accountingDeliveredDate,
          format: INT_DATE_FORMAT,
        })
      : null,
    issueDate: invoiceJson.issueDate
      ? tzNormalizeDate({
          date: invoiceJson.issueDate,
          format: INT_DATE_FORMAT,
        })
      : null,
    expirationDate: invoiceJson.expirationDate
      ? tzNormalizeDate({
          date: invoiceJson.expirationDate,
          format: INT_DATE_FORMAT,
        })
      : null,
    documentSeries: invoiceJson.documentSeries,
    documentCode: invoiceJson.documentCode,
    description: invoiceJson.description,
    summaryNumber: invoiceJson.summaryNumber,
    amount: invoiceJson.amount,
    itinerarySupplierFoodDetailList: invoiceJson.itinerarySupplierFoodDetailList
      ? invoiceJson.itinerarySupplierFoodDetailList.map((item) => item.id)
      : [],
  });

  const generateInitialSearchValues = (invoiceJson) => ({
    supplierId: {
      value: invoiceJson.supplierId,
      label: invoiceJson.supplierBusinessName,
    },
    fromDateString: invoiceJson.periodStartDate
      ? tzNormalizeDate({
          date: invoiceJson.periodStartDate,
          format: INT_DATE_FORMAT,
        })
      : null,
    toDateString: invoiceJson.periodEndDate
      ? tzNormalizeDate({
          date: invoiceJson.periodEndDate,
          format: INT_DATE_FORMAT,
        })
      : null,
  });

  const getPreSelectedIds = (invoiceJson) =>
    invoiceJson.itinerarySupplierFoodDetailList
      ? invoiceJson.itinerarySupplierFoodDetailList.map((item) => item.id)
      : [];

  const onSubmit = (formValues, selectedSupplier) => {
    if (!selectedSupplier) {
      toastr.error('Debe buscar un proveedor.');
      return;
    }

    if (
      !formValues.itinerarySupplierFoodDetailList ||
      formValues.itinerarySupplierFoodDetailList.length === 0
    ) {
      toastr.error('Debe seleccionar al menos un viaje.');
      return;
    }

    const newFormValues = {
      supplierId: formValues.supplierId,
      documentSeries: formValues.documentSeries,
      documentCode: formValues.documentCode,
      amount: formValues.amount,
      summaryNumber: formValues.summaryNumber,
      description: formValues.description,
      accountingDeliveredDate: formValues.accountingDeliveredDate
        ? tzNormalizeDate({
            date: formValues.accountingDeliveredDate,
          })
        : null,
      issueDate: formValues.issueDate
        ? tzNormalizeDate({
            date: formValues.issueDate,
          })
        : null,
      expirationDate: formValues.expirationDate
        ? tzNormalizeDate({
            date: formValues.expirationDate,
          })
        : null,
      periodStartDate: formValues.periodStartDate
        ? tzNormalizeDate({
            date: formValues.periodStartDate,
          })
        : null,
      periodEndDate: formValues.periodEndDate
        ? tzNormalizeDate({
            date: formValues.periodEndDate,
          })
        : null,
      itinerarySupplierFoodDetailList:
        formValues.itinerarySupplierFoodDetailList.map((item) => ({
          id: item,
        })),
    };

    dispatchPutItineraryFoodInvoice(itineraryFoodInvoiceId, newFormValues);
  };

  let content;

  if (loading) content = <Loader />;
  else if (itineraryFoodInvoice.isEmpty()) {
    content = <NoDataResource returnPage={ITINERARY_FOOD_INVOICE_PATH} />;
  } else {
    const invoiceJson = itineraryFoodInvoice.toJS();

    content = (
      <ItineraryFoodInvoiceForm
        edit
        onSubmit={onSubmit}
        initialValues={generateInitialValues(invoiceJson)}
        initialSearchValues={generateInitialSearchValues(invoiceJson)}
        preSelectedIds={getPreSelectedIds(invoiceJson)}
      />
    );
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Factura de Alimentación"
      subtitle="Editar factura de alimentación"
      content={content}
    />
  );
};

EditItineraryFoodInvoice.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchGetItineraryFoodInvoice: PropTypes.func.isRequired,
  dispatchClearItineraryFoodInvoice: PropTypes.func.isRequired,
  dispatchPutItineraryFoodInvoice: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  itineraryFoodInvoice: PropTypes.instanceOf(Immutable.Map).isRequired,
};

EditItineraryFoodInvoice.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchGetItineraryFoodInvoice: getItineraryFoodInvoice,
  dispatchClearItineraryFoodInvoice: clearItineraryFoodInvoice,
  dispatchPutItineraryFoodInvoice: putItineraryFoodInvoice,
};

const mapStateToProps = (
  { FoodUnit },
  {
    match: {
      params: { id },
    },
  },
) => ({
  breadcrumbs: [
    ...FoodUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Facturas de Alimentación',
      href: ITINERARY_FOOD_INVOICE_PATH,
    },
    {
      text: 'Ver',
      href: `${ITINERARY_FOOD_INVOICE_PATH}/${id}`,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],
  itineraryFoodInvoice: FoodUnit.ItineraryFoodInvoice.getIn([
    'current',
    'content',
  ]),
  loading: !FoodUnit.ItineraryFoodInvoice.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditItineraryFoodInvoice);
